'use client'
import { useEffect, useRef } from 'react'

import type { BuildingBlockPostType, RoundtripPostType } from '@typings'
import { GA4Events } from '@utilities/ga4-helpers/GA4Events.helpers'

import { useSettings } from '@/contexts/settings.context'
import { useDestinationSlug } from '@/hooks/useDestinationSlug'

interface Props {
  item: RoundtripPostType | BuildingBlockPostType
}

// Fires the GA4 dl_view_item event once per product view. Keyed on the full view context
// (id + link + destination), not item.id alone: a multi-destination product keeps one Sanity
// _id across destinations and categories, while its link, destination (country) and
// category_name (region) differ per view. An id-only guard would swallow the re-fire when Next
// reuses this instance across a client navigation and leave GA4 on the first view's URL/region.
// The ref guard still prevents a double fire in StrictMode.
export const RaProductViewTracker = ({ item }: Props) => {
  const { GTM_ID, isFamilySite } = useSettings()
  const destination = useDestinationSlug()
  const analytics = new GA4Events({ mode: 'client', gtmId: GTM_ID, destination, isFamilySite })
  const trackedKey = useRef<string | null>(null)
  const viewKey = `${item.id}|${item.link}|${destination ?? ''}`

  useEffect(() => {
    if (trackedKey.current === viewKey) return
    trackedKey.current = viewKey
    // No page_referrer: document.referrer is stale on client navigation; the accurate
    // previous in-app path is already reported on the page_view event.
    if (item.type === 'rundreise') {
      analytics.viewRoundtrip(item, '', destination ?? '')
    } else {
      analytics.viewBuildingBlock(item, '', destination ?? '')
    }
  }, [viewKey])

  return null
}
